/* eslint-disable react-hooks/exhaustive-deps */
import { useEffect, useMemo, useState } from "react";
import { FormInput, FormSelect, PrimaryButton } from "../../components";
import { withProtectedRoute } from "../../utils/HOC/withProtectedRoute";
import {
  IParcelHandoffFilters,
  useParcelHandoffs,
} from "../../store/useParcelHandoffs";
import { usePartners } from "../../store/usePartners";
import { useDropLocations } from "../../store/useDropLocations";
import { useAuth } from "../../store/useAuth";
import { ParcelHandoffsTable } from "./ParcelHandoffsTable";

const LIMIT = 30;

function ParcelHandoffsScreen() {
  /** Hooks */
  const {
    parcelHandoffs,
    getParcelHandoffs,
    clearParcelHandoffs,
    hasNextPage,
    isLoading,
    error,
  } = useParcelHandoffs();
  const { couriers, getCouriers } = usePartners();
  const { dropLocations, getDropLocations } = useDropLocations();
  const currentUser = useAuth((state) => state.user);
  // Store admins are pinned to their own store by the backend query; the
  // filter is disabled here so the UI does not offer a choice that has no
  // effect. The backend remains the thing that enforces it.
  const isAdmin = [1, 7].includes(Number(currentUser?.user_role ?? 0));

  /** State */
  const [page, setPage] = useState(1);
  const [filters, setFilters] = useState<IParcelHandoffFilters>({
    search: "",
    carrierPartnerId: "",
    direction: "",
    locationId: "",
    dateFrom: "",
    dateTo: "",
  });

  /** Effects */
  useEffect(() => {
    getCouriers();
    getDropLocations();
    return () => clearParcelHandoffs();
  }, []);

  // Any filter change restarts at page 1 — appending page 2 of a new filter
  // onto page 1 of the old one would show a list that never existed.
  useEffect(() => {
    const timer = setTimeout(() => {
      setPage(1);
      getParcelHandoffs(1, LIMIT, filters);
    }, 300);
    return () => clearTimeout(timer);
  }, [
    filters.search,
    filters.carrierPartnerId,
    filters.direction,
    filters.locationId,
    filters.dateFrom,
    filters.dateTo,
  ]);

  /** Callbacks */
  const setFilter = (key: keyof IParcelHandoffFilters, value: string) =>
    setFilters((prev) => ({ ...prev, [key]: value }));

  const loadMore = () => {
    // Guarded rather than merely disabled: a fast double click can fire twice
    // before React re-renders the button.
    if (isLoading) return;
    const next = page + 1;
    setPage(next);
    getParcelHandoffs(next, LIMIT, filters);
  };

  const carrierOptions = useMemo(
    () => [
      { value: "", label: "All carriers" },
      ...(couriers ?? []).map((c) => ({
        value: c?._id as string,
        label: c?.name as string,
      })),
    ],
    [couriers]
  );

  const locationOptions = useMemo(
    () => [
      { value: "", label: isAdmin ? "All locations" : "Your store" },
      ...(dropLocations ?? []).map((l) => ({
        value: l?._id as string,
        label: (l?.name ?? l?.city ?? l?._id) as string,
      })),
    ],
    [dropLocations, isAdmin]
  );

  return (
    <div>
      <div className="flex w-full justify-between">
        <h1 className="font-satoshi text-2xl font-bold text-dark mb-11">
          Parcel Handoffs
        </h1>
      </div>

      <div className="w-full px-7 pt-7 bg-white rounded-xl border-gray-border drop-shadow-none mb-4">
        <div className="grid grid-cols-1 gap-x-4 sm:grid-cols-3">
          <FormInput
            label="Customer"
            placeholder="Search by name, email or ID"
            value={filters.search}
            onChange={(e) => setFilter("search", e.target.value)}
          />
          <FormSelect
            label="Carrier"
            value={filters.carrierPartnerId}
            options={carrierOptions}
            onChange={(e) => setFilter("carrierPartnerId", e.target.value)}
          />
          {/* A registration can carry both sides, so each value matches every
              registration that has at least one parcel on that side. */}
          <FormSelect
            label="Direction"
            value={filters.direction}
            options={[
              { value: "", label: "All directions" },
              { value: "PICKUP", label: "With pickups" },
              { value: "DROPOFF", label: "With drop-offs" },
            ]}
            onChange={(e) => setFilter("direction", e.target.value)}
          />
          <FormSelect
            label="Location"
            value={filters.locationId}
            options={locationOptions}
            disabled={!isAdmin}
            onChange={(e) => setFilter("locationId", e.target.value)}
          />
          <FormInput
            label="From"
            type="date"
            value={filters.dateFrom}
            onChange={(e) => setFilter("dateFrom", e.target.value)}
          />
          <FormInput
            label="To"
            type="date"
            value={filters.dateTo}
            onChange={(e) => setFilter("dateTo", e.target.value)}
          />
        </div>
        {/* Store staff only ever see their own store, enforced by the backend
            query — the location filter narrows that further, never widens it. */}
      </div>

      <div className="w-full px-7 py-7 bg-white rounded-xl border-gray-border drop-shadow-none">
        {error ? (
          <p className="text-center font-satoshi text-base text-red-600">
            {error}
          </p>
        ) : isLoading && !parcelHandoffs?.length ? (
          <p className="text-center font-satoshi text-base text-dark">
            Loading parcel handoffs…
          </p>
        ) : !parcelHandoffs?.length ? (
          <p className="text-center font-satoshi text-base text-dark">
            No parcel handoffs match these filters.
          </p>
        ) : (
          <ParcelHandoffsTable />
        )}
      </div>

      {hasNextPage ? (
        <div className="flex flex-row w-full items-center justify-end mt-3">
          <PrimaryButton
            onClick={loadMore}
            disabled={isLoading}
            label={isLoading ? "Loading…" : "Load more"}
            className="h-10"
          />
        </div>
      ) : null}
    </div>
  );
}

export default withProtectedRoute(ParcelHandoffsScreen);
